import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { format, subDays, parseISO } from "date-fns";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarIcon, TrendingUp, Users, Building2, Zap } from "lucide-react";

const PALETTE = ["#7c3aed", "#a855f7", "#2563eb", "#0ea5e9", "#06b6d4", "#f59e0b", "#ec4899", "#10b981", "#ef4444", "#8b5cf6", "#14b8a6", "#f97316"];
const QUICK_RANGES = [
  { label: "7D", days: 7 },
  { label: "30D", days: 30 },
  { label: "90D", days: 90 },
  { label: "1Y", days: 365 },
  { label: "All", days: "all" },
];

const fmtDay = (d) => (d ? format(d, "yyyy-MM-dd") : "");
const shortDate = (s) => {
  try { return format(new Date(s + "T00:00:00"), "MMM d"); } catch { return s; }
};

const ChartCard = ({ title, subtitle, children, delay = 0, testid }) => (
  <div
    className="content-card chart-card"
    data-testid={testid}
    style={{
      padding: "20px",
      animation: `chartFadeUp 0.5s ease ${delay}s both`,
    }}
  >
    <div style={{ marginBottom: "8px" }}>
      <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#0f172a" }}>{title}</h3>
      {subtitle && <p style={{ fontSize: "12px", color: "#94a3b8", marginTop: "2px" }}>{subtitle}</p>}
    </div>
    {children}
  </div>
);

function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ from: subDays(new Date(), 30), to: new Date() });
  const [activeQuick, setActiveQuick] = useState(30);
  const [scanType, setScanType] = useState("all");
  const [industry, setIndustry] = useState("all");
  const [industries, setIndustries] = useState([]);
  const [earliestDate, setEarliestDate] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      // If the range end is 'today', send an exact 'now' timestamp so records
      // saved earlier today (stored in UTC) aren't cut off by a local/UTC day boundary.
      const endIsToday = range.to && fmtDay(range.to) === fmtDay(new Date());
      const params = {
        date_from: fmtDay(range.from),
        date_to: endIsToday ? new Date().toISOString() : fmtDay(range.to),
        tz_offset: new Date().getTimezoneOffset(),
      };
      if (scanType !== "all") params.scan_type = scanType;
      if (industry !== "all") params.industry = industry;
      const res = await axios.get(`${API}/analytics/dashboard`, { params });
      setData(res.data);
      setIndustries(res.data.available_industries || []);
      if (res.data.earliest_date) setEarliestDate(res.data.earliest_date);
      setLoading(false);
    } catch (e) {
      console.error("analytics error", e);
      toast.error("Failed to load analytics");
      setLoading(false);
    }
  }, [range.from, range.to, scanType, industry]);

  useEffect(() => { load(); }, [load]);

  const applyQuick = (days) => {
    setActiveQuick(days);
    if (days === "all") {
      const from = earliestDate ? parseISO(earliestDate) : subDays(new Date(), 365);
      setRange({ from, to: new Date() });
    } else {
      setRange({ from: subDays(new Date(), days - 1), to: new Date() });
    }
  };

  if (loading && !data) {
    return (
      <div className="loading"><div className="spinner"></div><p>Loading analytics...</p></div>
    );
  }

  const s = data?.summary || {};
  const priorityColor = (p) => (p === "High" ? "#ef4444" : p === "Low" ? "#94a3b8" : "#f59e0b");
  const priorityData = (data?.leads_by_priority || []).map((d) => ({ ...d, fill: priorityColor(d.priority) }));
  const cityData = (data?.leads_by_city || []).map((d, i) => ({ ...d, fill: PALETTE[(i + 2) % PALETTE.length] }));
  const summaryCards = [
    { label: "Total Leads", value: s.total_leads || 0, icon: Users, grad: "linear-gradient(135deg,#7c3aed,#a855f7)" },
    { label: "New TX Businesses", value: s.new_tx_businesses || 0, icon: Building2, grad: "linear-gradient(135deg,#2563eb,#0ea5e9)" },
    { label: "Scans Run", value: s.total_scans || 0, icon: TrendingUp, grad: "linear-gradient(135deg,#0ea5e9,#06b6d4)" },
    { label: "CRM Synced", value: s.total_synced || 0, icon: Zap, grad: "linear-gradient(135deg,#f59e0b,#ec4899)" },
  ];

  const tooltipStyle = {
    background: "#0f172a", border: "none", borderRadius: "10px",
    color: "#fff", fontSize: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
  };

  const isEmpty = (arr, keys) =>
    !arr || arr.length === 0 || !arr.some((r) => keys.some((k) => (r[k] || 0) > 0));

  // Multicolor tooltip: each entry gets a colored dot + colored text (like the Scans chart)
  const MultiTooltip = ({ active, payload, nameKey }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div style={{ ...tooltipStyle, padding: "8px 12px" }}>
        {payload.map((e, i) => {
          const color = e.payload?.fill || e.color || PALETTE[i % PALETTE.length];
          const name = e.payload?.[nameKey] ?? e.name;
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px", color, fontSize: "12px", fontWeight: 600 }}>
              <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: color, display: "inline-block" }} />
              <span>{name}: {e.value}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div data-testid="dashboard">
      <style>{`
        @keyframes chartFadeUp { from { opacity: 0; transform: translateY(16px);} to { opacity: 1; transform: translateY(0);} }
        .chart-card { transition: box-shadow .25s ease, transform .25s ease; }
        .chart-card:hover { box-shadow: 0 12px 32px rgba(124,58,237,0.12); transform: translateY(-2px); }
        .quick-range-btn { transition: all .2s ease; }
      `}</style>

      <div className="page-header" style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: "16px" }}>
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-description">A vibrant overview of your leads, scans and pipeline · <span style={{ color: "#94a3b8", fontStyle: "italic" }}>times shown in your local time</span></p>
        </div>
      </div>

      {/* Filters */}
      <div className="content-card" style={{ padding: "16px", marginBottom: "20px", display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }} data-testid="dashboard-filters">
        <div style={{ display: "flex", gap: "6px" }}>
          {QUICK_RANGES.map((q) => (
            <button
              key={q.label}
              className="quick-range-btn"
              data-testid={`quick-range-${q.label}`}
              onClick={() => applyQuick(q.days)}
              style={{
                padding: "8px 14px", borderRadius: "10px", fontSize: "13px", fontWeight: 600,
                border: "1px solid " + (activeQuick === q.days ? "#7c3aed" : "#e2e8f0"),
                background: activeQuick === q.days ? "linear-gradient(135deg,#7c3aed,#a855f7)" : "#fff",
                color: activeQuick === q.days ? "#fff" : "#475569", cursor: "pointer",
              }}
            >
              {q.label}
            </button>
          ))}
        </div>

        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <button
              data-testid="date-range-trigger"
              style={{
                display: "flex", alignItems: "center", gap: "8px", padding: "8px 14px",
                borderRadius: "10px", border: "1px solid #e2e8f0", background: "#fff",
                fontSize: "13px", fontWeight: 600, color: "#475569", cursor: "pointer",
              }}
            >
              <CalendarIcon size={15} />
              {range.from && range.to
                ? `${format(range.from, "MMM d")} – ${format(range.to, "MMM d, yyyy")}`
                : "Pick dates"}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={range}
              onSelect={(r) => {
                if (r) {
                  setRange(r);
                  setActiveQuick(null);
                  if (r.from && r.to) setPickerOpen(false);
                }
              }}
              numberOfMonths={2}
              defaultMonth={range.from}
            />
          </PopoverContent>
        </Popover>

        <Select value={scanType} onValueChange={(v) => setScanType(v)}>
          <SelectTrigger style={{ width: "170px" }} data-testid="filter-scan-type">
            <SelectValue placeholder="Scan type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All scan types</SelectItem>
            <SelectItem value="manual">Manual (Texas)</SelectItem>
            <SelectItem value="advanced">Advanced Search</SelectItem>
            <SelectItem value="discover">Discover</SelectItem>
          </SelectContent>
        </Select>

        <Select value={industry} onValueChange={(v) => setIndustry(v)}>
          <SelectTrigger style={{ width: "200px" }} data-testid="filter-industry">
            <SelectValue placeholder="Industry" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All industries</SelectItem>
            {industries.map((ind) => (
              <SelectItem key={ind} value={ind}>{ind}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary cards */}
      <div className="stats-grid" style={{ marginBottom: "20px" }}>
        {summaryCards.map((c, i) => {
          const Icon = c.icon;
          return (
            <div
              key={c.label}
              className="stat-card"
              data-testid={`summary-${c.label.toLowerCase().replace(/ /g, "-")}`}
              style={{ animation: `chartFadeUp 0.4s ease ${i * 0.06}s both`, position: "relative", overflow: "hidden" }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div className="stat-label">{c.label}</div>
                  <div className="stat-value">{c.value}</div>
                </div>
                <div style={{ width: "40px", height: "40px", borderRadius: "12px", background: c.grad, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
                  <Icon size={20} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: "20px" }}>
        <ChartCard title="Leads over time" subtitle="Verified leads discovered" delay={0.05} testid="chart-leads-over-time">
          {isEmpty(data?.leads_over_time, ["leads"]) ? <EmptyChart /> : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data?.leads_over_time || []}>
              <defs>
                <linearGradient id="gLeads" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#7c3aed" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#7c3aed" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11, fill: "#94a3b8" }} minTickGap={24} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} width={28} />
              <Tooltip contentStyle={tooltipStyle} labelFormatter={shortDate} />
              <Area type="monotone" dataKey="leads" stroke="#7c3aed" strokeWidth={2.5} fill="url(#gLeads)" animationDuration={900} />
            </AreaChart>
          </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Scans over time" subtitle="By scan type" delay={0.1} testid="chart-scans-over-time">
          {isEmpty(data?.scans_over_time, ["total"]) ? <EmptyChart /> : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data?.scans_over_time || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11, fill: "#94a3b8" }} minTickGap={24} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} width={28} />
              <Tooltip contentStyle={tooltipStyle} labelFormatter={shortDate} />
              <Legend wrapperStyle={{ fontSize: "12px" }} />
              <Bar dataKey="manual" stackId="a" name="Manual" fill="#2563eb" radius={[0, 0, 0, 0]} animationDuration={900} />
              <Bar dataKey="advanced" stackId="a" name="Advanced" fill="#7c3aed" animationDuration={900} />
              <Bar dataKey="discover" stackId="a" name="Discover" fill="#06b6d4" radius={[4, 4, 0, 0]} animationDuration={900} />
            </BarChart>
          </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="New Texas businesses" subtitle="From the Texas Business Tracker" delay={0.15} testid="chart-tx-businesses">
          {isEmpty(data?.tx_businesses_over_time, ["businesses"]) ? <EmptyChart /> : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data?.tx_businesses_over_time || []}>
              <defs>
                <linearGradient id="gTx" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11, fill: "#94a3b8" }} minTickGap={24} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} width={28} />
              <Tooltip contentStyle={tooltipStyle} labelFormatter={shortDate} />
              <Area type="monotone" dataKey="businesses" name="New businesses" stroke="#0ea5e9" strokeWidth={2.5} fill="url(#gTx)" animationDuration={900} />
            </AreaChart>
          </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Leads by industry" subtitle="Where your leads come from" delay={0.2} testid="chart-industry">
          {(data?.leads_by_industry || []).length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data?.leads_by_industry || []} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <YAxis type="category" dataKey="industry" tick={{ fontSize: 11, fill: "#475569" }} width={130} />
                <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: "#f8fafc" }} labelStyle={{ color: "#cbd5e1", fontWeight: 600 }} cursor={{ fill: "rgba(124,58,237,0.06)" }} />
                <Bar dataKey="count" name="Leads" radius={[0, 6, 6, 0]} animationDuration={900}>
                  {(data?.leads_by_industry || []).map((e, i) => (
                    <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Enrichment breakdown" subtitle="Email vs phone coverage" delay={0.25} testid="chart-enrichment">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={data?.enrichment_breakdown || []}
                dataKey="value" nameKey="name" cx="50%" cy="50%"
                innerRadius={55} outerRadius={90} paddingAngle={3}
                animationDuration={900}
              >
                {(data?.enrichment_breakdown || []).map((e, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: "12px" }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leads by priority & top cities" subtitle="Distribution across your pipeline" delay={0.3} testid="chart-priority-city">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={priorityData} dataKey="count" nameKey="priority" cx="50%" cy="50%" outerRadius={80} animationDuration={900} label={(e) => e.priority}>
                  {priorityData.map((e, i) => (
                    <Cell key={i} fill={e.fill} />
                  ))}
                </Pie>
                <Tooltip content={<MultiTooltip nameKey="priority" />} />
              </PieChart>
            </ResponsiveContainer>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={cityData} layout="vertical" margin={{ left: 6 }}>
                <XAxis type="number" allowDecimals={false} hide />
                <YAxis type="category" dataKey="city" tick={{ fontSize: 10, fill: "#475569" }} width={80} />
                <Tooltip content={<MultiTooltip nameKey="city" />} cursor={{ fill: "rgba(37,99,235,0.06)" }} />
                <Bar dataKey="count" name="Leads" radius={[0, 5, 5, 0]} animationDuration={900}>
                  {cityData.map((e, i) => (
                    <Cell key={i} fill={e.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>
    </div>
  );
}

const EmptyChart = () => (
  <div style={{ height: 260, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#94a3b8", gap: "8px" }}>
    <TrendingUp size={32} />
    <p style={{ fontSize: "13px" }}>No data for the selected filters yet</p>
  </div>
);

export default Dashboard;
